import React, { useEffect, useState } from "react";
import api, { fileUrl, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, Badge, Modal, Button, Field } from "@/components/ui-kit";
import { User, Pencil, Camera, CheckCircle2, XCircle, ClipboardCheck, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const CoachStatsModal = ({ coach, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/coaches/${coach.source}/${coach.id}/stats`)
      .then((r) => setStats(r.data)).catch(() => setStats(null)).finally(() => setLoading(false));
  }, [coach]);
  const att = stats?.attendance || {};
  const av = stats?.availability || {};
  const Stat = ({ label, value, color = "text-foreground", tid }) => (
    <div className="rounded-xl bg-secondary/50 border border-border p-4 text-center">
      <p className={`font-display text-3xl font-black ${color}`} data-testid={tid}>{value}</p>
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{label}</p>
    </div>
  );
  return (
    <Modal open onClose={onClose} title={`${coach.first_name} ${coach.last_name}`}>
      <div data-testid="coach-stats-modal">
        {loading ? <Empty text="Chargement..." /> : !stats ? <Empty text="Statistiques indisponibles." /> : (
          <div className="space-y-6">
            {!stats.linked_user && (
              <p className="text-xs text-yellow-500 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                Ce coach n'est pas relié à un compte de connexion : les statistiques de disponibilité peuvent être incomplètes.
              </p>
            )}
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><ClipboardCheck size={14} className="text-primary" /> Présence aux séances</p>
              <div className="grid grid-cols-4 gap-3">
                <Stat label="Séances" value={att.total ?? 0} tid="stat-att-total" />
                <Stat label="Présent" value={att.present ?? 0} color="text-primary" tid="stat-att-present" />
                <Stat label="Absences" value={att.absent ?? 0} color="text-destructive" tid="stat-att-absent" />
                <Stat label="Taux" value={`${att.rate ?? 0}%`} tid="stat-att-rate" />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5"><CalendarCheck size={14} className="text-primary" /> Disponibilités déclarées</p>
              <div className="grid grid-cols-4 gap-3">
                <Stat label="Réponses" value={av.answered ?? 0} tid="stat-av-answered" />
                <Stat label="Disponible" value={av.available ?? 0} color="text-yellow-500" tid="stat-av-available" />
                <Stat label="Indispo." value={av.unavailable ?? 0} tid="stat-av-unavailable" />
                <Stat label="Retenu" value={av.confirmed ?? 0} color="text-primary" tid="stat-av-confirmed" />
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                <Badge color="green"><CheckCircle2 size={12} /> {av.confirmed ?? 0} confirmé(s) par l'admin</Badge>
                <Badge color="red"><XCircle size={12} /> {av.rejected ?? 0} non retenu(s)</Badge>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default function Coaches() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [statsCoach, setStatsCoach] = useState(null);
  const [bio, setBio] = useState("");
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/coaches").then((r) => setCoaches(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openEdit = (c) => { setEditing(c); setBio(c.bio || ""); setPhoto(c.photo || ""); };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { setPhoto(await uploadImage(file)); }
    catch { toast.error("Envoi de la photo échoué"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/coaches/${editing.source}/${editing.id}/profile`, { photo, bio });
      toast.success("Profil du coach mis à jour");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="coaches-page">
      <PageHeader title="Nos Coachs" subtitle="L'équipe technique qui encadre et fait grandir nos joueurs." />

      {loading ? (
        <Empty text="Chargement..." />
      ) : coaches.length ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {coaches.map((c, i) => (
            <div key={`${c.source}-${c.id}`} data-testid="coach-card"
              className="group relative bg-card border border-border rounded-2xl overflow-hidden flex flex-col animate-fade-up hover:border-primary/60 transition-colors"
              style={{ animationDelay: `${i * 60}ms` }}>
              <div className="relative aspect-[4/3] bg-secondary overflow-hidden">
                {c.photo ? (
                  <img src={fileUrl(c.photo)} alt={`${c.first_name} ${c.last_name}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <User size={56} strokeWidth={1.2} />
                  </div>
                )}
                <div className="absolute top-3 left-3"><Badge color="blue">Coach</Badge></div>
                {isAdmin && (
                  <button data-testid={`edit-coach-btn-${c.id}`} onClick={() => openEdit(c)}
                    className="absolute top-3 right-3 p-2 rounded-full bg-black/60 text-white hover:bg-primary transition-colors" title="Modifier le profil">
                    <Pencil size={15} />
                  </button>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                {isAdmin ? (
                  <button type="button" data-testid={`coach-name-btn-${c.id}`} onClick={() => setStatsCoach(c)}
                    className="text-left font-display text-lg font-bold uppercase tracking-tight leading-tight hover:text-primary transition-colors">
                    {c.first_name} {c.last_name}
                  </button>
                ) : (
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight leading-tight">
                    {c.first_name} {c.last_name}
                  </h3>
                )}
                {(c.category || c.position) && (
                  <p className="text-xs uppercase tracking-widest text-primary font-bold mt-1">
                    {[c.category, c.position].filter(Boolean).join(" · ")}
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">
                  {c.bio || "Présentation à venir."}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty text="Aucun coach pour le moment. Attribuez la fonction « Coach » à un membre ou un utilisateur." />
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Modifier le profil du coach">
        {editing && (
          <div>
            <p className="text-sm text-muted-foreground mb-4"><b className="text-foreground">{editing.first_name} {editing.last_name}</b></p>
            <Field label="Photo">
              <div className="flex items-center gap-4">
                {photo ? (
                  <img src={fileUrl(photo)} alt="aperçu" className="w-16 h-16 rounded-full object-cover border border-border" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center text-muted-foreground"><User size={26} /></div>
                )}
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary border border-border text-sm font-semibold cursor-pointer hover:border-primary transition-colors">
                  <Camera size={15} /> {uploading ? "Envoi..." : (photo ? "Changer la photo" : "Ajouter une photo")}
                  <input data-testid="coach-photo-input" type="file" accept="image/*" className="hidden" onChange={onPhoto} disabled={uploading} />
                </label>
              </div>
            </Field>
            <Field label="Biographie">
              <textarea data-testid="coach-bio-input" rows={4} value={bio} onChange={(e) => setBio(e.target.value)}
                placeholder="Parcours, expérience, catégorie encadrée..."
                className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary resize-y" />
            </Field>
            <Button data-testid="save-coach-profile-btn" onClick={save} disabled={saving || uploading} className="w-full mt-2">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        )}
      </Modal>
      {statsCoach && <CoachStatsModal coach={statsCoach} onClose={() => setStatsCoach(null)} />}
    </div>
  );
}
